import LogicFlow from '@logicflow/core';
import '@logicflow/core/dist/style/index.css';

import { MiniMap } from '@logicflow/extension';
import { Snapshot } from '@logicflow/extension';
import { SelectionSelect } from '@logicflow/extension';
import { NodeResize } from '@logicflow/extension';
import '@logicflow/extension/lib/style/index.css';

import { registerShapes } from './shapes/index';
import { registerEdges } from './edges/index';
import { BpmnReader } from '../serialization/BpmnReader';
import { BpmnWriter } from '../serialization/BpmnWriter';
import { DefaultPropsInterceptor } from '../interceptors/DefaultPropsInterceptor';
import { PropertiesPanel } from '../properties/PropertiesPanel';
import { ContextPad } from './ContextPad';
import { ReplaceMenu } from './ReplaceMenu';
import { Palette } from './Palette';
import { BpmnValidator } from './validation/BpmnValidator';
import { ContainmentBehavior } from '../interceptors/ContainmentBehavior';
import { mergeTheme, THEMES } from './theme';
import en from '../i18n/en.json';
import es from '../i18n/es.json';

const I18N = { en, es };

export class EpxFlow {
  constructor({ container, propertiesContainer, paletteContainer, locale = 'en', grid = false, theme } = {}) {
    this._locale = locale;
    // Initialize locale entry if it doesn't exist (e.g. 'pt') so addTranslations()
    // called later updates the SAME object that _i18n points to.
    if (!I18N[locale]) I18N[locale] = { ...I18N.en };
    this._i18n = I18N[locale];
    this._eventHandlers = {};
    this._customThemes = {};

    // Resolve theme: accept a string name (e.g. 'dark') or an object with overrides
    const resolvedTheme = typeof theme === 'string' ? (this._customThemes[theme] || THEMES[theme] || THEMES.default) : theme;
    this._theme = mergeTheme(resolvedTheme);

    // Grid state — always init LogicFlow with a grid so the DOM element exists;
    // visibility is controlled via CSS/DOM after render.
    this._gridVisible = !!grid;
    this._gridSize = (grid && typeof grid === 'object' && grid.size) ? grid.size : 20;

    const containerEl = typeof container === 'string'
      ? document.querySelector(container)
      : container;

    const gridColor = this._theme.grid && this._theme.grid.color ? this._theme.grid.color : '#e0e0e0';

    this._lf = new LogicFlow({
      container: containerEl,
      grid: { size: this._gridSize, visible: true, type: 'mesh', config: { color: gridColor, thickness: 1 } },
      keyboard: { enabled: true },
      edgeType: 'epx:SequenceFlow',
      plugins: [NodeResize, MiniMap, Snapshot, SelectionSelect],
      isSilentMode: false,
    });

    // Store theme on graphModel so shape/edge models can access it
    this._lf.graphModel.__epxTheme = this._theme;

    // Apply canvas background — .lf-graph has background:#fff in LogicFlow's CSS,
    // so we must target it directly in addition to the outer container.
    if (this._theme.canvas && this._theme.canvas.background && containerEl) {
      containerEl.style.background = this._theme.canvas.background;
      const lfGraph = containerEl.querySelector('.lf-graph');
      if (lfGraph) lfGraph.style.background = this._theme.canvas.background;
    }

    // Inject theme CSS variables
    this._applyThemeCSSVars();

    // Patch LogicFlow internal text theme so labels use the correct color
    this._patchLfTextTheme();

    registerShapes(this._lf);
    registerEdges(this._lf);

    // Patch LogicFlow bug: graphModel.moveNodes crashes when nodesMap[id] is undefined
    // (happens when selectNodes contains stale references to deleted/non-existent nodes).
    this._patchMoveNodes();

    this._reader = new BpmnReader();
    this._writer = new BpmnWriter();

    this._interceptor = new DefaultPropsInterceptor(this._lf);
    this._containment = new ContainmentBehavior(this._lf);
    ContainmentBehavior.injectStyles();

    if (propertiesContainer) {
      const propEl = typeof propertiesContainer === 'string'
        ? document.querySelector(propertiesContainer)
        : propertiesContainer;
      this._propertiesPanel = new PropertiesPanel(this._lf, propEl, this._i18n);
    }

    this._contextPad = new ContextPad(this._lf, this._i18n);
    this._contextPad.setContainment(this._containment);
    this._replaceMenu = new ReplaceMenu(this._lf, this._i18n);
    this._contextPad.setReplaceMenu(this._replaceMenu);

    if (paletteContainer) {
      const palEl = typeof paletteContainer === 'string'
        ? document.querySelector(paletteContainer)
        : paletteContainer;
      this._palette = new Palette(this._lf, palEl, this._i18n, {
        onFitView:          () => this.fit(),
        onAutoLayout:       () => this.autoLayout().catch(err => console.error('[epx-flow] autoLayout error:', err)),
        onSelectionChanged: (elements) => this._emit('selection.changed', { newSelection: elements }),
      });
    }

    this._validator = new BpmnValidator(this._lf, this._i18n);
    BpmnValidator.injectStyles();

    this._bindEvents();
    this._bindKeyboardShortcuts();
    this._setupTextLayer();
    this._lf.render();

    // Apply initial grid visibility. LogicFlow always gets a grid config so
    // the .lf-grid SVG element exists in the DOM; we hide it if grid was off.
    if (!this._gridVisible) {
      const gridEl = containerEl && containerEl.querySelector('.lf-grid');
      if (gridEl) gridEl.style.display = 'none';
      this._lf.graphModel.gridSize = 1;
    }
  }

  // ── Text layer ─────────────────────────────────────────────────────────
  // Task shapes render text as SVG <text> elements inside their shape <g>.
  // Because SVG paints in DOM order, a node rendered later can cover the text
  // of an earlier node.  We fix this by maintaining a dedicated <g
  // class="epx-text-layer"> appended LAST inside .lf-base so it always paints
  // above every shape.  A CSS rule hides the originals; the layer holds clones
  // kept in sync via LogicFlow events.

  _setupTextLayer() {
    // CSS: hide original SVG text inside node shape groups; the text layer
    // provides the visible copies.
    if (!document.getElementById('epx-text-layer-css')) {
      const style = document.createElement('style');
      style.id = 'epx-text-layer-css';
      style.textContent =
        '.epx-tl-active .lf-base > g.lf-node text { visibility: hidden !important; }\n' +
        '.epx-text-layer { pointer-events: none; }\n' +
        '.epx-text-layer text { visibility: visible !important; }';
      document.head.appendChild(style);
    }

    // Events that require a text-layer sync
    const SYNC_EVENTS = [
      'graph:rendered',
      'node:add', 'node:dnd-add', 'node:delete',
      'node:drag', 'node:drop',
      'node:properties-change',
      'node:resize',
      'history:change',
      'text:update',
    ];
    SYNC_EVENTS.forEach(evt => this._lf.on(evt, () => this._scheduleTextSync()));
  }

  _scheduleTextSync() {
    if (this._textSyncRaf) return;
    this._textSyncRaf = requestAnimationFrame(() => {
      this._textSyncRaf = null;
      this._syncTextLayer();
    });
  }

  _syncTextLayer() {
    const svg = this._lf.container && this._lf.container.querySelector('svg');
    const base = svg && svg.querySelector('.lf-base');
    if (!base) return;

    // Mark SVG so the CSS rule that hides originals takes effect
    svg.classList.add('epx-tl-active');

    // Ensure text layer is the last child of .lf-base (paints on top of everything)
    let layer = base.querySelector('.epx-text-layer');
    if (!layer) {
      layer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      layer.setAttribute('class', 'epx-text-layer');
    }
    base.appendChild(layer); // moves to end if already present

    // Rebuild: for each node <g class="lf-node">, prefer the custom SVG text
    // marked with data-epx="1" when present; fall back to LogicFlow's overlay
    // text for shapes that don't render their own (events, gateways).
    // This prevents rendering both texts when a node has custom rendering AND
    // a stale LogicFlow overlay (e.g. after editText from the context pad).
    layer.innerHTML = '';
    const nodeEls = base.querySelectorAll(':scope > g.lf-node');
    for (const nodeEl of nodeEls) {
      const customTexts = nodeEl.querySelectorAll('text[data-epx="1"]');
      if (customTexts.length > 0) {
        customTexts.forEach(t => layer.appendChild(t.cloneNode(true)));
      } else {
        nodeEl.querySelectorAll('text').forEach(t => layer.appendChild(t.cloneNode(true)));
      }
    }
  }

  // ── End text layer ─────────────────────────────────────────────────────

  _patchMoveNodes() {
    const gm = this._lf.graphModel;
    if (!gm || typeof gm.moveNodes !== 'function') return;
    const original = gm.moveNodes.bind(gm);
    gm.moveNodes = function (nodeIds, deltaX, deltaY, isIgnoreRule) {
      // Filter out IDs that don't exist in nodesMap to prevent crash
      const validIds = nodeIds.filter(id => gm.nodesMap && gm.nodesMap[id]);
      if (validIds.length === 0) return;
      return original(validIds, deltaX, deltaY, isIgnoreRule);
    };
  }

  _bindEvents() {
    this._lf.on('node:click', ({ data }) => {
      this._emit('selection.changed', { newSelection: [data] });
      if (this._propertiesPanel) {
        this._propertiesPanel.show(data);
      }
    });

    this._lf.on('edge:click', ({ data }) => {
      this._emit('selection.changed', { newSelection: [data] });
      if (this._propertiesPanel) {
        this._propertiesPanel.show(data);
      }
    });

    this._lf.on('blank:click', () => {
      this._emit('selection.changed', { newSelection: [] });
      if (this._propertiesPanel) {
        this._propertiesPanel.hide();
      }
    });

    // SelectionSelect (lasso) emits 'selection:selected' with an array of elements (may be empty)
    this._lf.on('selection:selected', (elements) => {
      this._emit('selection.changed', { newSelection: elements || [] });
      if (this._propertiesPanel) {
        this._propertiesPanel.hide();
      }
    });

    this._lf.on('node:properties-change', ({ id, properties }) => {
      this._emit('element.changed', { element: { id, properties } });
      this._emit('diagram.changed');
    });

    // Sync resize dimensions into properties.width / properties.height
    // so BpmnWriter can serialize them correctly.
    this._lf.on('node:resize', ({ newNodeSize }) => {
      const { id, width, height } = newNodeSize;
      const model = this._lf.getNodeModelById(id);
      if (model) {
        model.setProperties({ width, height });
      }
      this._emit('diagram.changed');
    });

    this._lf.on('history:change', () => {
      this._emit('diagram.changed');
    });
  }

  _bindKeyboardShortcuts() {
    const lf = this._lf;

    this._keydownHandler = (e) => {
      // Don't intercept when focus is in an input, textarea, or contenteditable
      // (e.g. DMN table cells edited via <span contenteditable="true">).
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) return;

      const isCtrl = e.ctrlKey || e.metaKey;

      if (isCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        lf.undo();
      } else if (isCtrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        lf.redo();
      } else if (isCtrl && e.key === 'a') {
        e.preventDefault();
        const { nodes, edges } = lf.getGraphRawData ? lf.getGraphRawData() : lf.getGraphData();
        nodes.forEach(n => lf.selectElementById(n.id, true));
        edges.forEach(edge => lf.selectElementById(edge.id, true));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        const { nodes, edges } = lf.getSelectElements(true);
        [...nodes, ...edges].forEach(el => lf.deleteElement(el.id));
      } else if (isCtrl && e.key === 's') {
        e.preventDefault();
        this._emit('save.requested');
      }
    };

    document.addEventListener('keydown', this._keydownHandler);
  }

  // Undo/Redo
  undo() { this._lf.undo(); }
  redo() { this._lf.redo(); }

  // MiniMap
  showMiniMap() {
    const ext = this._lf.extension.miniMap;
    if (ext) ext.show();
  }
  hideMiniMap() {
    const ext = this._lf.extension.miniMap;
    if (ext) ext.hide();
  }

  // Snapshot
  async getSnapshotBase64(backgroundColor = '#fff') {
    const ext = this._lf.extension.snapshot;
    if (ext) return ext.getSnapshotBase64(backgroundColor);
    return null;
  }
  async getSnapshotBlob(backgroundColor = '#fff') {
    const ext = this._lf.extension.snapshot;
    if (ext) return ext.getSnapshotBlob(backgroundColor);
    return null;
  }
  downloadSnapshot(fileName = 'diagram', backgroundColor = '#fff') {
    const ext = this._lf.extension.snapshot;
    if (ext) ext.getSnapshot(fileName, backgroundColor);
  }

  async importXML(xmlString, { autoFit = true } = {}) {
    const graphData = await this._reader.read(xmlString);
    // Preserve original definitions metadata for round-trip fidelity
    this._importMeta = graphData.meta || {};

    // Two-phase render: nodes first so nodesMap is fully populated, then edges one-by-one.
    // lf.render() initialises edge models before all node models are in nodesMap, which
    // causes a crash accessing .anchors on an undefined node (e.g. AI-hallucinated IDs).
    const allEdges = graphData.edges || [];
    this._lf.render({ ...graphData, edges: [] });
    let skippedEdges = 0;
    for (const edge of allEdges) {
      try {
        this._lf.addEdge(edge);
      } catch (e) {
        skippedEdges++;
        console.warn('[epx-flow] importXML: skipped edge', edge.id, '-', e.message);
      }
    }
    if (skippedEdges > 0) {
      console.warn(`[epx-flow] importXML: ${skippedEdges} edge(s) skipped`);
    }
    // Defer fit, containment rebuild and text-layer sync until after LogicFlow finishes rendering
    setTimeout(() => {
      if (autoFit) this.fit();
      try {
        this._containment.rebuild();
        this._containment.snapAllLanesToPools();
      } catch (e) {
        console.warn('[epx-flow] ContainmentBehavior.rebuild() error:', e);
      }
      this._syncTextLayer();
    }, 150);
  }

  async exportXML() {
    const graphData = this._lf.getGraphData();
    // Pass original metadata so targetNamespace and definitionsId are preserved
    graphData.meta = this._importMeta || {};
    const { xml, warnings } = await this._writer.write(graphData);
    return { xml, warnings };
  }

  exportSVG() {
    const container = this._lf.container;
    const svgEl = container && container.querySelector('svg');
    if (!svgEl) throw new Error('SVG element not found in canvas');

    // Get tight bounding box of actual content in canvas coordinates (before pan/zoom transform)
    const baseLayer = svgEl.querySelector('.lf-base');
    if (!baseLayer) throw new Error('.lf-base layer not found in SVG');
    const bbox = baseLayer.getBBox();
    const padding = 20;
    const vbX = bbox.x - padding;
    const vbY = bbox.y - padding;
    const vbW = bbox.width + 2 * padding;
    const vbH = bbox.height + 2 * padding;

    const clone = svgEl.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    clone.setAttribute('width', vbW);
    clone.setAttribute('height', vbH);
    clone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);

    // Reset the pan/zoom transform so elements render at their canvas coordinates,
    // which the viewBox above crops precisely to the content area
    const contentGroup = clone.lastElementChild;
    if (contentGroup) {
      contentGroup.removeAttribute('transform');
      contentGroup.style.transform = 'matrix(1, 0, 0, 1, 0, 0)';
    }

    // Collect all relevant CSS rules and inline them
    const styleText = Array.from(document.styleSheets)
      .flatMap(sheet => {
        try { return Array.from(sheet.cssRules); } catch { return []; }
      })
      .map(rule => rule.cssText)
      .join('\n');

    if (styleText) {
      const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      styleEl.textContent = styleText;
      clone.insertBefore(styleEl, clone.firstChild);
    }

    return new XMLSerializer().serializeToString(clone);
  }

  downloadSVG(fileName = 'diagram') {
    const svgString = this.exportSVG();
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }

  on(event, handler) {
    if (!this._eventHandlers[event]) {
      this._eventHandlers[event] = [];
    }
    this._eventHandlers[event].push(handler);
  }

  off(event, handler) {
    if (!this._eventHandlers[event]) return;
    this._eventHandlers[event] = this._eventHandlers[event].filter(h => h !== handler);
  }

  _emit(event, data) {
    const handlers = this._eventHandlers[event];
    if (handlers) {
      handlers.forEach(h => h(data));
    }
  }

  zoom(level) {
    this._lf.zoom(level);
  }

  fit() {
    const gm = this._lf.graphModel;
    if (!gm || !gm.transformModel) {
      this._lf.fitView(20);
      return;
    }

    // Calculate the real bounding box of all content
    const rect = gm.getVirtualRectSize();
    const { virtualRectWidth: cw, virtualRectHeight: ch,
            virtualRectCenterPositionX: cx, virtualRectCenterPositionY: cy } = rect;

    if (!cw || !ch) {
      this._lf.fitView(20);
      return;
    }

    // Dynamically lower the zoom floor so very large diagrams can fit (Opción 5)
    const padding = 40;
    const neededScale = Math.min(
      (gm.width - padding) / cw,
      (gm.height - padding) / ch,
    );
    const minScale = Math.max(0.02, Math.min(0.2, neededScale * 0.8));
    this._lf.setZoomMiniSize(minScale);

    // Reset transform, then fit
    this._lf.fitView(20);

    // If fitView produced a good result, we're done
    if (gm.transformModel.SCALE_X >= minScale) return;

    // Fallback: manually position the viewport centered on the content
    gm.transformModel.SCALE_X = neededScale;
    gm.transformModel.SCALE_Y = neededScale;
    gm.transformModel.TRANSLATE_X = (gm.width / 2) - cx * neededScale;
    gm.transformModel.TRANSLATE_Y = (gm.height / 2) - cy * neededScale;
  }

  /**
   * Get the current resolved theme object.
   */
  getTheme() {
    return this._theme;
  }

  /**
   * Change the theme at runtime. Accepts a theme name string or partial theme object.
   * Re-renders the diagram to apply new colors.
   */
  /**
   * Register a custom theme by name. Can be called before or after init.
   * Registered themes are available via setTheme('myTheme').
   */
  registerTheme(name, themeObj) {
    this._customThemes[name] = themeObj;
  }

  setTheme(theme) {
    const resolvedTheme = typeof theme === 'string' ? (this._customThemes[theme] || THEMES[theme] || THEMES.default) : theme;
    this._theme = mergeTheme(resolvedTheme);
    this._lf.graphModel.__epxTheme = this._theme;

    // Apply canvas background — .lf-graph has background:#fff in LogicFlow's CSS,
    // so we must target it directly in addition to the outer container.
    const containerEl = this._lf.container;
    const canvasBg = this._theme.canvas && this._theme.canvas.background;
    if (canvasBg && containerEl) {
      containerEl.style.background = canvasBg;
      const lfGraph = containerEl.querySelector('.lf-graph');
      if (lfGraph) lfGraph.style.background = canvasBg;
    }

    // Sync grid color with new theme — clear cached color so _applyGridPattern
    // picks up the theme value, then re-render the pattern.
    this._gridColor = undefined;
    if (containerEl) {
      const gridEl = containerEl.querySelector('.lf-grid');
      if (gridEl) this._applyGridPattern(gridEl);
    }

    // Update Palette if present
    if (this._palette && typeof this._palette.updateTheme === 'function') {
      this._palette.updateTheme(this._theme);
    }

    // Patch LogicFlow's internal theme so node/edge text uses the correct color
    this._patchLfTextTheme();

    // Apply CSS vars for panel theming
    this._applyThemeCSSVars();

    // Force nodes/edges to re-render.
    // __epxTheme is not a MobX observable, so lf.render() alone may not
    // trigger reactive updates when graph data is unchanged.
    // Setting a lightweight counter property on each model IS reactive:
    // getShape()/getEdgeStyle() read model.properties, so any properties
    // change causes those methods to be called again with the new theme.
    this._themeVersion = (this._themeVersion || 0) + 1;
    const _tv = this._themeVersion;
    this._lf.graphModel.nodes.forEach(n => n.setProperty('__tv', _tv));
    this._lf.graphModel.edges.forEach(e => {
      if (typeof e.setProperty === 'function') e.setProperty('__tv', _tv);
    });
    this._emit('theme.changed', { theme: this._theme });
    // Re-sync text layer after theme re-renders all nodes
    this._scheduleTextSync();
  }

  // ── Grid API ───────────────────────────────────────────────────────────────

  /**
   * Rebuild the SVG pattern child element for the current grid type.
   * Called internally whenever type, size, color or thickness changes.
   */
  _applyGridPattern(gridEl) {
    const svgNS   = 'http://www.w3.org/2000/svg';
    const pattern = gridEl && gridEl.querySelector('pattern');
    if (!pattern) return;

    const size      = this._gridSize      || 20;
    const color     = this._gridColor     || (this._theme.grid && this._theme.grid.color) || '#e0e0e0';
    const thickness = this._gridThickness || 1;
    const type      = this._gridType      || 'mesh';

    // Remove existing shape children (path or rect), keep nothing else
    Array.from(pattern.children).forEach(el => el.remove());

    pattern.setAttribute('width',  size);
    pattern.setAttribute('height', size);

    if (type === 'dot') {
      const dotSize = Math.max(1, Math.min(thickness * 2, size / 4));
      const rect = document.createElementNS(svgNS, 'rect');
      rect.setAttribute('width',  dotSize);
      rect.setAttribute('height', dotSize);
      rect.setAttribute('rx', dotSize / 2);
      rect.setAttribute('ry', dotSize / 2);
      rect.setAttribute('fill', color);
      pattern.appendChild(rect);
    } else {
      // mesh — two crossing lines
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d',            `M ${size} 0 H0 M0 0 V0 ${size}`);
      path.setAttribute('fill',         'none');
      path.setAttribute('stroke',       color);
      path.setAttribute('stroke-width', thickness);
      pattern.appendChild(path);
    }
  }

  /**
   * Configure the grid at runtime. All params are optional.
   * @param {object} opts
   * @param {boolean} [opts.visible]   - show or hide the grid
   * @param {string}  [opts.type]      - 'mesh' (lines) | 'dot' (points)
   * @param {number}  [opts.size]      - cell size in px (e.g. 10, 20, 40)
   * @param {string}  [opts.color]     - grid color (CSS color string)
   * @param {number}  [opts.thickness] - line stroke-width / dot radius factor
   */
  setGrid({ visible, type, size, color, thickness } = {}) {
    const containerEl = this._lf && this._lf.container;
    if (!containerEl) return;
    const gridEl = containerEl.querySelector('.lf-grid');

    let patternDirty = false;

    if (type !== undefined) {
      this._gridType = type;
      patternDirty = true;
    }
    if (size !== undefined && size > 0) {
      this._gridSize = size;
      if (this._gridVisible) this._lf.graphModel.gridSize = size;
      patternDirty = true;
    }
    if (color !== undefined) {
      this._gridColor = color;
      patternDirty = true;
    }
    if (thickness !== undefined) {
      this._gridThickness = thickness;
      patternDirty = true;
    }

    if (patternDirty && gridEl) {
      this._applyGridPattern(gridEl);
    }

    if (visible !== undefined) {
      this._gridVisible = !!visible;
      if (gridEl) gridEl.style.display = visible ? '' : 'none';
      this._lf.graphModel.gridSize = visible ? (this._gridSize || 20) : 1;
    }
  }

  /** Show or hide the grid. */
  setGridVisible(visible) {
    this.setGrid({ visible });
  }

  /** Returns true if the grid is currently visible. */
  isGridVisible() {
    return !!this._gridVisible;
  }

  /**
   * Patch LogicFlow's internal graphModel.theme so that:
   * - nodeText.color reflects our theme's text.fill (fixes event/gateway labels)
   * - edgeText.background.fill is transparent (removes the white SVG rect on edge labels)
   * - edgeText.color reflects our theme's text.fill
   *
   * Called on construction and on every setTheme().
   */
  _patchLfTextTheme() {
    const lft = this._lf.graphModel && this._lf.graphModel.theme;
    if (!lft) return;
    const textFill = (this._theme.text && this._theme.text.fill) || '#333';
    if (lft.nodeText) lft.nodeText.color = textFill;
    if (lft.edgeText) {
      lft.edgeText.color = textFill;
      if (lft.edgeText.background) lft.edgeText.background.fill = 'transparent';
    }
  }

  _applyThemeCSSVars() {
    const p = this._theme.panel;
    if (!p) return;
    let styleEl = document.getElementById('epx-theme-vars');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'epx-theme-vars';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      :root {
        --epx-panel-bg: ${p.background};
        --epx-panel-header-bg: ${p.headerBackground};
        --epx-panel-header-border: ${p.headerBorder};
        --epx-panel-text: ${p.text};
        --epx-panel-text-secondary: ${p.textSecondary};
        --epx-panel-text-muted: ${p.textMuted};
        --epx-panel-border: ${p.border};
        --epx-panel-input-bg: ${p.inputBackground};
        --epx-panel-input-border: ${p.inputBorder};
        --epx-panel-hover-bg: ${p.hoverBackground};
        --epx-panel-accent-bg: ${p.accentBackground};
        --epx-panel-accent-border: ${p.accentBorder};
        --epx-panel-accent-text: ${p.accentText};
      }
    `;
  }

  // ── ELK layout engine (lazy-initialized) ──────────────────────────────

  _getElk() {
    if (!this._elkPromise) {
      // Lazy-load elk-api.js and pair it with ELK's official worker.
      // Webpack 5 resolves the new URL() and emits elk-worker.min.js as a
      // separate chunk, so the ELK engine (~1.5 MB) is only downloaded the
      // first time the user clicks Auto Layout.
      this._elkPromise = import('elkjs/lib/elk-api.js').then(({ default: ELK }) =>
        new ELK({ workerUrl: new URL('elkjs/lib/elk-worker.min.js', import.meta.url) })
      );
    }
    return this._elkPromise;
  }

  _elkLayout(graph) {
    return this._getElk().then(elk => elk.layout(graph));
  }

  /**
   * Auto-layout the diagram using ELK Layered (left-to-right hierarchical
   * layout with orthogonal edge routing).
   *
   * - Flat diagrams: runs ELK on all flow nodes.
   * - Diagrams with Participants/Lanes: runs ELK per-lane, then stacks lanes
   *   and resizes Pools to fit.
   *
   * @param {{ hGap?: number, vGap?: number }} [options]
   * @returns {Promise<void>}
   */
  async autoLayout({ hGap = 80, vGap = 70 } = {}) {
    const lf = this._lf;
    const { nodes, edges } = lf.getGraphData();
    if (!nodes || nodes.length === 0) return;

    const CONTAINERS = new Set([
      'epx:Participant', 'epx:Lane', 'epx:SubProcess', 'epx:Transaction', 'epx:Group',
    ]);
    const LANE_PAD = 20;
    const MIN_LANE_H = 150;
    const MIN_POOL_W = 500;
    const POOL_HDR = ContainmentBehavior.POOL_HEADER_WIDTH; // 30px

    /**
     * Move a node to an absolute position and keep its text label in sync.
     */
    const moveTo = (id, newX, newY) => {
      const m = lf.getNodeModelById(id);
      if (!m) return;
      const dx = newX - m.x;
      const dy = newY - m.y;
      m.x = newX;
      m.y = newY;
      if (m.text && typeof m.text === 'object') {
        m.text.x = (typeof m.text.x === 'number' ? m.text.x : newX) + dx;
        m.text.y = (typeof m.text.y === 'number' ? m.text.y : newY) + dy;
      }
    };

    // Element types whose label renders OUTSIDE (below) the shape.
    const BELOW_LABEL_TYPES = new Set([
      'epx:StartEvent', 'epx:EndEvent', 'epx:IntermediateEvent',
      'epx:IntermediateThrowEvent', 'epx:BoundaryEvent',
      'epx:ExclusiveGateway', 'epx:ParallelGateway', 'epx:InclusiveGateway',
      'epx:ComplexGateway', 'epx:EventBasedGateway',
    ]);

    /**
     * Measure an external label's rendered size.
     * Uses canvas.measureText for WYSIWYG accuracy; falls back to a
     * character-count estimate (useful in SSR / test environments).
     *
     * @param {string} text
     * @param {number} wrapWidth  max line width in px before wrapping
     * @returns {{ width: number, height: number }}
     */
    const measureLabel = (() => {
      let ctx = null;
      try { ctx = document.createElement('canvas').getContext('2d'); } catch (_) {}
      const FONT_SIZE = 12;
      const LINE_H = Math.ceil(FONT_SIZE * 1.5); // 18 px per line
      return (text, wrapWidth) => {
        if (!text) return { width: 0, height: 0 };
        if (ctx) {
          ctx.font = `${FONT_SIZE}px sans-serif`;
          const words = text.split(/\s+/);
          let lines = 1, lineW = 0, maxW = 0;
          for (const word of words) {
            const ww  = ctx.measureText(word).width;
            const sep = lineW > 0 ? ctx.measureText(' ').width : 0;
            if (lineW > 0 && lineW + sep + ww > wrapWidth) {
              maxW = Math.max(maxW, lineW);
              lines++;
              lineW = ww;
            } else {
              lineW += sep + ww;
            }
          }
          return { width: Math.ceil(Math.min(Math.max(maxW, lineW), wrapWidth)), height: lines * LINE_H + 4 };
        }
        // Fallback: ~0.55 × font-size per character
        const charsPerLine = Math.max(1, Math.floor(wrapWidth / (FONT_SIZE * 0.55)));
        const lines = Math.ceil(text.length / charsPerLine);
        return { width: wrapWidth, height: lines * LINE_H + 4 };
      };
    })();

    /**
     * Run ELK Layered on a set of node IDs with orthogonal edge routing.
     * Returns { positions: Map<id,{x,y}>, waypoints: Map<edgeId,{x,y}[]>, width, height }
     * where positions are center-based (LogicFlow convention) relative to ELK graph origin.
     */
    const runElk = async (nodeIds, edgesForLayout) => {
      const idSet = new Set(nodeIds);

      // One EAST output port and one WEST input port per node so ELK
      // routes all outgoing edges from the right side and incoming from the left.
      const buildPorts = (id) => {
        const ports = [];
        if (edgesForLayout.some(e => e.sourceNodeId === id && idSet.has(e.targetNodeId)))
          ports.push({ id: `${id}__out`, properties: { 'port.side': 'EAST' } });
        if (edgesForLayout.some(e => e.targetNodeId === id && idSet.has(e.sourceNodeId)))
          ports.push({ id: `${id}__in`, properties: { 'port.side': 'WEST' } });
        return ports;
      };

      const graph = {
        id: 'root',
        layoutOptions: {
          'elk.algorithm': 'layered',
          'elk.direction': 'RIGHT',
          // Keep all nodes in a single layout pass — without this, ELK treats
          // disconnected subgraphs (e.g. nodes reachable only via a back-edge
          // after cycle breaking) as separate components and stacks them into
          // independent clusters, causing the "two groups" appearance.
          'elk.separateConnectedComponents': 'false',
          'elk.layered.spacing.nodeNodeBetweenLayers': String(hGap),
          'elk.spacing.nodeNode': String(vGap),
          // Gap between an external label and its host node (events / gateways).
          'elk.spacing.labelNode': '6',
          'elk.edgeRouting': 'ORTHOGONAL',
          'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
          'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
          // Respect the original model order when deciding which edges to reverse
          // to break cycles. GREEDY picks arbitrary edges; GREEDY_MODEL_ORDER
          // prefers edges that run "backwards" relative to the model sequence,
          // so loops in BPMN (e.g. retry paths) end up pointing left instead of
          // flipping the entire diagram topology.
          'elk.layered.cycleBreaking.strategy': 'GREEDY_MODEL_ORDER',
          'elk.layered.unnecessaryBendpoints': 'true',
          'elk.layered.spacing.edgeEdgeBetweenLayers': '15',
          'elk.spacing.edgeNode': '20',
          'elk.padding': `[top=${LANE_PAD}, left=${LANE_PAD}, bottom=${LANE_PAD}, right=${LANE_PAD}]`,
        },
        children: nodeIds.map(id => {
          const m = lf.getNodeModelById(id);
          // FIXED_SIDE tells ELK to honour our explicit EAST/WEST port assignments
          // instead of freely reordering ports to minimise crossings.
          const node = {
            id,
            width: m?.width || 120,
            height: m?.height || 60,
            ports: buildPorts(id),
            layoutOptions: { 'elk.portConstraints': 'FIXED_SIDE' },
          };
          // Anchor start events to the first layer and end events to the last.
          // This guarantees the natural BPMN left→right reading order even when
          // the cycle breaker reverses other edges.
          if (m?.type === 'epx:StartEvent') {
            node.layoutOptions['elk.layered.layering.layerConstraint'] = 'FIRST';
          } else if (m?.type === 'epx:EndEvent') {
            node.layoutOptions['elk.layered.layering.layerConstraint'] = 'LAST';
          }
          // For events/gateways the label renders OUTSIDE (below) the shape.
          // Pass it via ELK's native labels array so ELK accounts for the label
          // dimensions in its spacing calculations — no need to inflate height manually.
          const labelText = m?.text?.value || '';
          if (m && BELOW_LABEL_TYPES.has(m.type) && labelText) {
            const wrapW = Math.max((m.width || 50) * 2, 100);
            const { width: lw, height: lh } = measureLabel(labelText, wrapW);
            node.labels = [{ width: lw, height: lh }];
            node.layoutOptions['elk.nodeLabels.placement'] = 'OUTSIDE BOTTOM CENTER';
          }
          return node;
        }),
        edges: edgesForLayout
          .filter(e => idSet.has(e.sourceNodeId) && idSet.has(e.targetNodeId))
          .map(e => ({ id: e.id, sources: [`${e.sourceNodeId}__out`], targets: [`${e.targetNodeId}__in`] })),
      };

      const laid = await this._elkLayout(graph);

      // ELK top-left → LogicFlow center coords
      const positions = new Map();
      for (const n of laid.children || []) {
        const m = lf.getNodeModelById(n.id);
        positions.set(n.id, {
          x: n.x + (m?.width || n.width) / 2,
          y: n.y + (m?.height || n.height) / 2,
        });
      }

      // ELK edge sections → waypoint arrays (relative to ELK graph origin)
      const waypoints = new Map();
      for (const e of laid.edges || []) {
        const s = e.sections && e.sections[0];
        if (!s) continue;
        const pts = [s.startPoint, ...(s.bendPoints || []), s.endPoint];
        if (pts.length >= 2) waypoints.set(e.id, pts.map(p => ({ x: p.x, y: p.y })));
      }

      return { positions, waypoints, width: laid.width || 0, height: laid.height || 0 };
    };

    // Node types that use an external label below/outside the shape.
    // After layout moves these nodes, reapply the correct label offset.
    const LABEL_OFFSET_TYPES = {
      'epx:StartEvent':           m => m.r  + 14,
      'epx:EndEvent':             m => m.r  + 14,
      'epx:IntermediateEvent':    m => m.r  + 14,
      'epx:IntermediateThrowEvent': m => m.r + 14,
      'epx:BoundaryEvent':        m => m.r  + 14,
      'epx:ExclusiveGateway':     m => m.ry + 18,
      'epx:ParallelGateway':      m => m.ry + 18,
      'epx:InclusiveGateway':     m => m.ry + 18,
      'epx:ComplexGateway':       m => m.ry + 18,
      'epx:EventBasedGateway':    m => m.ry + 18,
    };

    /**
     * After layout moves nodes, reset external labels (events, gateways) to
     * their canonical below-shape position so they never overlap the shape.
     */
    const resetNodeLabels = (nodeList) => {
      nodeList.forEach(n => {
        const offsetFn = LABEL_OFFSET_TYPES[n.type];
        if (!offsetFn) return;
        const m = lf.getNodeModelById(n.id);
        if (!m || !m.text || !m.text.value) return;
        m.text.y = m.y + offsetFn(m);
      });
    };

    /**
     * Apply ELK-computed routes to LogicFlow edge models.
     * For edges with ELK waypoints: set pointsList directly (orthogonal routes).
     * For edges without (cross-scope, annotations): fall back to initPoints().
     */
    const applyEdgeRoutes = (edgeList, waypoints, handledIds, offsetX = 0, offsetY = 0) => {
      edgeList.forEach(e => {
        const em = lf.getEdgeModelById(e.id);
        if (!em) return;
        if (typeof em.setAnchors === 'function') em.setAnchors();
        const pts = waypoints.get(e.id);
        if (pts && pts.length >= 2) {
          em.pointsList = pts.map(p => ({ x: offsetX + p.x, y: offsetY + p.y }));
          // Sync startPoint/endPoint with the ELK path so that LogicFlow's
          // moveStartPoint/moveEndPoint (called when a connected node is dragged)
          // operates on valid coordinates instead of stale pre-layout values,
          // which would cause NaN in updatePoints().
          em.startPoint = { ...em.pointsList[0] };
          em.endPoint   = { ...em.pointsList[em.pointsList.length - 1] };
          // initPoints() with a non-empty pointsList only serializes pointsList
          // into the SVG `points` string — it does NOT recalculate the route.
          // This is required because the renderer reads model.points, not
          // model.pointsList, so without this call the display stays stale/NaN.
          if (typeof em.initPoints === 'function') em.initPoints();
          handledIds.add(e.id);
        } else {
          if (typeof em.initPoints === 'function') em.initPoints();
        }
        if (typeof em.resetTextPosition === 'function') em.resetTextPosition();
      });
    };

    this._emit('layout.start', {});
    try {
      const handledEdgeIds = new Set();
      const participants = nodes.filter(n => n.type === 'epx:Participant');

      if (participants.length > 0) {
        // ── Diagrams with Pools ──────────────────────────────────────
        for (const pool of participants) {
          const poolModel = lf.getNodeModelById(pool.id);
          if (!poolModel) continue;

          const laneIds = this._containment.getLanesForPool(pool.id);

          if (laneIds.length === 0) {
            // Pool without lanes — layout direct children
            const childIds = this._containment.getChildren(pool.id)
              .filter(id => { const m = lf.getNodeModelById(id); return m && !CONTAINERS.has(m.type); });
            if (childIds.length === 0) continue;

            const childIdSet = new Set(childIds);
            const childEdges = edges.filter(e => childIdSet.has(e.sourceNodeId) && childIdSet.has(e.targetNodeId));
            const { positions, waypoints, width, height } = await runElk(childIds, childEdges);

            const origPoolLeft = poolModel.x - poolModel.width / 2;
            const origPoolTop  = poolModel.y - poolModel.height / 2;
            const originX = origPoolLeft + POOL_HDR + LANE_PAD;
            const originY = origPoolTop  + LANE_PAD;

            childIds.forEach(id => { const pos = positions.get(id); if (pos) moveTo(id, originX + pos.x, originY + pos.y); });

            const newW = Math.max(MIN_POOL_W, width  + POOL_HDR + LANE_PAD * 2);
            const newH = Math.max(MIN_LANE_H, height + LANE_PAD * 2);
            poolModel.width  = newW;
            poolModel.height = newH;
            poolModel.x = origPoolLeft + newW / 2;
            poolModel.y = origPoolTop  + newH / 2;

            applyEdgeRoutes(childEdges, waypoints, handledEdgeIds, originX, originY);
            continue;
          }

          // Pool with lanes — layout each lane independently
          const laneResults = [];
          const nodeToLane = new Map(); // nodeId → laneId (for cross-lane routing)
          for (const laneId of laneIds) {
            const childIds = this._containment.getChildren(laneId)
              .filter(id => { const m = lf.getNodeModelById(id); return m && !CONTAINERS.has(m.type); });
            for (const id of childIds) nodeToLane.set(id, laneId);
            if (childIds.length === 0) {
              laneResults.push({ laneId, childIds, positions: new Map(), waypoints: new Map(), width: 0, height: 0 });
              continue;
            }
            const childIdSet = new Set(childIds);
            const childEdges = edges.filter(e => childIdSet.has(e.sourceNodeId) && childIdSet.has(e.targetNodeId));
            const result = await runElk(childIds, childEdges);
            laneResults.push({ laneId, childIds, childEdges, ...result });
          }

          const maxContentW = Math.max(...laneResults.map(r => r.width), 0);
          const newPoolW    = Math.max(MIN_POOL_W, maxContentW + POOL_HDR + LANE_PAD * 2);
          const laneW       = newPoolW - POOL_HDR;

          const poolLeft = poolModel.x - poolModel.width / 2;
          const poolTop  = poolModel.y - poolModel.height / 2;

          poolModel.width = newPoolW;
          poolModel.x = poolLeft + newPoolW / 2;

          let currentY = poolTop;

          for (const { laneId, childIds, childEdges = [], positions, waypoints, height } of laneResults) {
            const laneH = Math.max(MIN_LANE_H, height + LANE_PAD * 2);
            const laneModel = lf.getNodeModelById(laneId);
            if (laneModel) {
              laneModel.x      = poolLeft + POOL_HDR + laneW / 2;
              laneModel.y      = currentY + laneH / 2;
              laneModel.width  = laneW;
              laneModel.height = laneH;
              laneModel.setProperties({ width: laneW, height: laneH });
            }

            const originX = poolLeft + POOL_HDR + LANE_PAD;
            const originY = currentY + LANE_PAD;
            childIds.forEach(id => { const pos = positions.get(id); if (pos) moveTo(id, originX + pos.x, originY + pos.y); });
            applyEdgeRoutes(childEdges, waypoints, handledEdgeIds, originX, originY);

            currentY += laneH;
          }

          const totalH = currentY - poolTop;
          poolModel.height = Math.max(MIN_LANE_H * laneIds.length, totalH);
          poolModel.y = poolTop + poolModel.height / 2;

          // Route cross-lane edges with orthogonal Z-shaped paths now that
          // all nodes are in their final positions. The plain initPoints()
          // fallback would draw a straight diagonal that cuts through lane
          // dividers and other nodes.
          const poolContentRight = poolLeft + newPoolW - LANE_PAD;
          const poolNodeSet = new Set(nodeToLane.keys());
          edges.forEach(e => {
            if (handledEdgeIds.has(e.id)) return;
            if (!poolNodeSet.has(e.sourceNodeId) || !poolNodeSet.has(e.targetNodeId)) return;
            const srcLane = nodeToLane.get(e.sourceNodeId);
            const tgtLane = nodeToLane.get(e.targetNodeId);
            if (!srcLane || !tgtLane || srcLane === tgtLane) return;

            const em = lf.getEdgeModelById(e.id);
            if (!em) return;
            const srcM = lf.getNodeModelById(e.sourceNodeId);
            const tgtM = lf.getNodeModelById(e.targetNodeId);
            if (!srcM || !tgtM) return;

            if (typeof em.setAnchors === 'function') em.setAnchors();

            // Z-route: exit right from source → vertical bend at midX → enter left at target.
            // midX sits halfway between source-right and target-left for forward flows,
            // or hGap/2 past source-right for backward flows — clamped to pool content area.
            const sx = srcM.x + srcM.width / 2;
            const sy = srcM.y;
            const tx = tgtM.x - tgtM.width / 2;
            const ty = tgtM.y;
            const midX = Math.min(poolContentRight, sx < tx ? (sx + tx) / 2 : sx + hGap / 2);

            em.pointsList = [
              { x: sx,   y: sy },
              { x: midX, y: sy },
              { x: midX, y: ty },
              { x: tx,   y: ty },
            ];
            em.startPoint = { ...em.pointsList[0] };
            em.endPoint   = { ...em.pointsList[em.pointsList.length - 1] };
            if (typeof em.initPoints === 'function') em.initPoints();
            if (typeof em.resetTextPosition === 'function') em.resetTextPosition();
            handledEdgeIds.add(e.id);
          });
        }

        // Fallback: route any edges not handled by per-lane ELK
        // (cross-lane flows, message flows between pools, associations)
        edges.forEach(e => {
          if (handledEdgeIds.has(e.id)) return;
          const em = lf.getEdgeModelById(e.id);
          if (!em) return;
          if (typeof em.setAnchors === 'function') em.setAnchors();
          if (typeof em.initPoints === 'function') em.initPoints();
          if (typeof em.resetTextPosition === 'function') em.resetTextPosition();
        });

      } else {
        // ── Flat diagram (no Participants) ───────────────────────────
        const flowNodes = nodes.filter(n => !CONTAINERS.has(n.type));
        if (flowNodes.length === 0) return;

        const { positions, waypoints } = await runElk(flowNodes.map(n => n.id), edges);
        const START_X = 80, START_Y = 80;
        flowNodes.forEach(n => { const pos = positions.get(n.id); if (pos) moveTo(n.id, START_X + pos.x, START_Y + pos.y); });
        applyEdgeRoutes(edges, waypoints, handledEdgeIds, START_X, START_Y);
      }

      resetNodeLabels(nodes);
      this._emit('diagram.changed', {});
      this._scheduleTextSync();
      setTimeout(() => this.fit(), 100);

    } catch (err) {
      console.error('[epx-flow] autoLayout error:', err);
      this._emit('layout.error', { error: err });
    } finally {
      this._emit('layout.end', {});
    }
  }

  clear() {
    this._lf.clearData();
  }

  destroy() {
    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }
    if (this._palette) {
      this._palette.destroy();
    }
    if (this._replaceMenu) {
      this._replaceMenu.destroy();
    }
    if (this._contextPad) {
      this._contextPad.destroy();
    }
    if (this._propertiesPanel) {
      this._propertiesPanel.destroy();
    }
    if (typeof this._lf.destroy === 'function') {
      this._lf.destroy();
    } else {
      // Fallback: clear the container manually
      const container = this._lf.container;
      if (container) container.innerHTML = '';
    }
    if (this._elkPromise) {
      this._elkPromise.then(elk => elk.terminateWorker()).catch(() => {});
      this._elkPromise = null;
    }
  }

  /**
   * Validate the current diagram against BPMN 2.0 rules.
   * @param {{ showOverlays?: boolean }} options
   * @returns {{ errors: Array, warnings: Array }}
   */
  validate({ showOverlays = true } = {}) {
    if (showOverlays) {
      return this._validator.showOverlays();
    }
    return this._validator.validate();
  }

  /**
   * Remove all validation highlights from the canvas.
   */
  clearValidation() {
    this._validator.clearOverlays();
  }

  // ── Extension APIs ──────────────────────────────────────────────

  /**
   * Register a properties panel provider.
   * @param {object} provider - Must implement isApplicable(type) and getGroups(element)
   * @param {number} [priority=1000] - Higher priority = appears first
   */
  registerProvider(provider, priority) {
    if (this._propertiesPanel) {
      this._propertiesPanel.registerProvider(provider, priority);
    }
  }

  /**
   * Unregister a properties panel provider by reference or id.
   * @param {object|string} providerOrId
   */
  unregisterProvider(providerOrId) {
    if (this._propertiesPanel) {
      this._propertiesPanel.unregisterProvider(providerOrId);
    }
  }

  /**
   * Register a moddle extension descriptor for custom XML namespaces.
   * Must be called before importXML/exportXML.
   * @param {string} name - Extension name (e.g. 'sf')
   * @param {object} descriptor - JSON moddle descriptor
   */
  addModdleExtension(name, descriptor) {
    this._reader.addModdleExtension(name, descriptor);
    this._writer.addModdleExtension(name, descriptor);
  }

  /**
   * Register a reader plugin for custom property extraction from XML.
   * Plugin: { extractProperties(el, props) → void }
   * @param {object} plugin
   */
  addReaderExtension(plugin) {
    this._reader.addReaderPlugin(plugin);
  }

  /**
   * Register a writer plugin for custom property serialization to XML.
   * Plugin: { applyProperties(el, props, moddle) → void, applyEdgeProperties?(el, props, moddle) → void }
   * @param {object} plugin
   */
  addWriterExtension(plugin) {
    this._writer.addWriterPlugin(plugin);
  }

  /**
   * Register additional default properties for a given element type.
   * @param {string} elementType - e.g. 'epx:UserTask'
   * @param {object} defaults - Property defaults to merge
   */
  addDefaultProps(elementType, defaults) {
    this._interceptor.addDefaultProps(elementType, defaults);
  }

  /**
   * Merge additional translations into the i18n object.
   * @param {string} locale - e.g. 'en', 'es'
   * @param {object} translations - Key-value pairs to merge
   */
  addTranslations(locale, translations) {
    if (!I18N[locale]) {
      I18N[locale] = {};
    }
    Object.assign(I18N[locale], translations);
    // If this is the active locale, update the live i18n reference
    if (locale === this._locale) {
      Object.assign(this._i18n, translations);
    }
  }

  /**
   * Returns the names of all available themes (built-in + custom registered).
   * @returns {string[]}
   */
  getRegisteredThemes() {
    return [...Object.keys(THEMES), ...Object.keys(this._customThemes)];
  }

  /**
   * Returns the locale codes of all registered locales (built-in + external).
   * @returns {string[]}
   */
  getRegisteredLocales() {
    return Object.keys(I18N);
  }

  getLogicFlow() {
    return this._lf;
  }
}
